import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { OnboardingActions } from '@portal/actions';
import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';
import { Col, Menu, Popover, Row } from 'antd';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedAutocomplete from '@portal/components/AdvancedAutocomplete/AdvancedAutocomplete';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { onboarding } from '@portal/config/app.json';
import AdvancedCheckbox from '@portal/components/AdvancedCheckbox/AdvancedCheckbox';
import { MaritalStatus } from '@portal/enum/maritalStatusEnum';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import { maskCpf, unmaskField } from '@portal/services/masks';
import { CountriesEnum } from '@portal/models/enumerators/onboarding';

interface AdvancedAutocompleteItem {
  value: string;
  name: string;
}

interface IProps {
  occupationsData: models.InitialOccupationsData;
  setOccupationsData: (value: models.InitialOccupationsData) => void;
  setUserDataForm: (value: models.InitialOnboarding) => void;
  userData: models.InitialOnboarding;
}

const OnboardingCbo = ({
  setOccupationsData,
  occupationsData,
  setUserDataForm,
  userData,
}: IProps) => {
  const [company, setCompany] = useState('');
  const [paramCboOccupations, setParamCboOccupations] = useState<
    models.CboOccupations[]
  >([]);
  const dispatch = useDispatch();
  const [hasFiscalResidenciaOutside, setHasFiscalResidenciaOutside] =
    useState(false);
  const [isUsPerson, setIsUsPerson] = useState(false);

  const {
    occupationCbo: {
      cbo,
      cboSubGreatGrandsonOccupation,
      cboSubGreatGreatGrandsonOccupation,
      cboSubOccupation,
    },
    onboarding: { settings, countriesList, statesList, citiesList },
  } = useReduxState();

  const generateDropdownArray = (
    length: number,
    singularKey: string,
    pluralKey: string
  ) => {
    return Array.from({ length }, (_, index) => ({
      value: `${index + 1}`,
      name: `${index + 1} ${translate(
        index + 1 === 1 ? singularKey : pluralKey
      )}`,
    }));
  };

  const yearsArray: AdvancedAutocompleteItem[] = generateDropdownArray(
    100,
    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.YEAR',
    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.YEARS'
  );
  const monthsArray: AdvancedAutocompleteItem[] = generateDropdownArray(
    11,
    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.MONTH',
    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.MONTHS'
  );

  const handleSelect = (value: string) => {
    dispatch(OnboardingActions.setOccupationError(null));
    setParamCboOccupations([]);

    setOccupationsData({
      ...occupationsData,
      cbo: null,
      grandchild: null,
      greatGrandson: null,
      greatGreatFinish: null,
      subOccupation: null,
    });

    const foundOccupation = cbo?.find(
      (itOccupation) => itOccupation.id === value
    );
    if (foundOccupation) {
      setParamCboOccupations(foundOccupation.cboOccupations);

      setOccupationsData({
        ...occupationsData,
        cbo: {
          id: foundOccupation.id,
          name: foundOccupation.category,
          subOccupation: null,
        },
        grandchild: null,
        greatGrandson: null,
        greatGreatFinish: null,
        subOccupation: null,
        validationCbo:
          foundOccupation?.cboOccupations.length > 0 ? true : false,
      });
    }
  };

  const handleSubOccupation = (value: string) => {
    const foundOccupation = paramCboOccupations?.find(
      (itOccupation) => itOccupation.id === value
    );
    if (foundOccupation) {
      setOccupationsData({
        ...occupationsData,
        subOccupation: {
          id: foundOccupation.id,
          name: foundOccupation.name,
          subOccupationId: foundOccupation.subOccupationId,
        },
        grandchild: null,
        greatGrandson: null,
        greatGreatFinish: null,
      });

      dispatch(OnboardingActions.getSubGrandchild(foundOccupation?.id));
    }
  };

  const handleSubOccupationChild = (value: string) => {
    const foundOccupation = cboSubOccupation?.find(
      (itOccupation) => itOccupation.id === value
    );
    if (foundOccupation) {
      setOccupationsData({
        ...occupationsData,
        grandchild: {
          id: foundOccupation.id,
          name: foundOccupation.name,
          subOccupationId: foundOccupation.subOccupationId,
        },
        greatGrandson: null,
        greatGreatFinish: null,
      });

      dispatch(OnboardingActions.getSubGreatGrandson(foundOccupation.id));
    }
  };

  const handleSubOccupationGreatGreatGrandson = (value: string) => {
    const foundOccupation = cboSubGreatGrandsonOccupation?.find(
      (itOccupation) => itOccupation.id === value
    );
    if (foundOccupation) {
      setOccupationsData({
        ...occupationsData,
        greatGrandson: {
          id: foundOccupation.id,
          name: foundOccupation.name,
          subOccupationId: foundOccupation.subOccupationId,
        },
        greatGreatFinish: null,
      });

      dispatch(OnboardingActions.getSubGreatGreatGrandson(foundOccupation.id));
    }
  };

  const handleSubOccupationGreatFinish = (name: string) => {
    const foundOccupation = cboSubGreatGreatGrandsonOccupation?.find(
      (itOccupation) => itOccupation.id === name
    );

    if (foundOccupation) {
      setOccupationsData({
        ...occupationsData,
        greatGreatFinish: {
          id: foundOccupation.id,
          name: foundOccupation.name,
          subOccupationId: foundOccupation.subOccupationId,
        },
      });
    }
  };

  const handleStateChange = (value: string | number | null) => {
    if (typeof value === 'string') {
      setUserDataForm({
        ...userData,
        bornState: value,
      });
      OnboardingActions.getCities(value);
    }
  };

  useEffect(() => {
    dispatch(OnboardingActions.getCbo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(OnboardingActions.getCountries());
    dispatch(OnboardingActions.getStates());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOccupationsData(occupationsData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occupationsData]);

  useEffect(() => {
    if (userData.bornState) {
      dispatch(OnboardingActions.getCities(userData?.bornState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.bornState]);

  const PopOverContent = (
    <div className="panel-content-top-bar__right__user-dropdown__menu">
      <Menu>
        <Menu.ItemGroup
          title={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.FATCA.POPOVER'
          )}
        ></Menu.ItemGroup>
      </Menu>
    </div>
  );

  return (
    <div>
      <AdvancedForm>
        <Row className="create-account-user__rows create-account-user__col-margin-top">
          <Col className="create-account-user__full">
            <AdvancedAutocomplete
              variant="standard"
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.OCCUPATION'
              )}
              onChange={(value) =>
                typeof value === 'string' && handleSelect(value)
              }
              options={
                cbo?.map((line) => ({ name: line.category, value: line.id })) ||
                []
              }
            />
          </Col>

          {paramCboOccupations.length > 0 && (
            <Col className="create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.SUB_OCCUPATIONS'
                )}
                onChange={(value) =>
                  typeof value === 'string' && handleSubOccupation(value)
                }
                options={paramCboOccupations.map((line) => ({
                  name: line.name,
                  value: line.id,
                }))}
              />
            </Col>
          )}

          {cboSubOccupation && cboSubOccupation.length > 0 ? (
            <Col className="create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.SUB_OCCUPATIONS'
                )}
                onChange={(value) =>
                  typeof value === 'string' && handleSubOccupationChild(value)
                }
                options={cboSubOccupation.map((line) => ({
                  name: line.name,
                  value: line.id,
                }))}
              />
            </Col>
          ) : null}

          {cboSubGreatGrandsonOccupation &&
          cboSubGreatGrandsonOccupation.length > 0 ? (
            <Col className="create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.SUB_OCCUPATIONS'
                )}
                onChange={(value) =>
                  typeof value === 'string' &&
                  handleSubOccupationGreatGreatGrandson(value)
                }
                options={cboSubGreatGrandsonOccupation.map((line) => ({
                  name: line.name,
                  value: line.id,
                }))}
              />
            </Col>
          ) : null}

          {cboSubGreatGreatGrandsonOccupation &&
          cboSubGreatGreatGrandsonOccupation.length > 0 ? (
            <Col className="create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.SUB_OCCUPATIONS'
                )}
                onChange={(value) =>
                  typeof value === 'string' &&
                  handleSubOccupationGreatFinish(value)
                }
                options={cboSubGreatGreatGrandsonOccupation.map((line) => ({
                  name: line.name,
                  value: line.id,
                }))}
              />
            </Col>
          ) : null}

          <Col className="create-account-user__full">
            <AdvancedInput
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.COMPANY'
              )}
              value={company}
              variant="standard"
              onChange={(value) => {
                setCompany(value);
                setOccupationsData({
                  ...occupationsData,
                  company: value,
                });
              }}
            />
          </Col>
        </Row>

        {onboarding.onboardingOccupancyTime ? (
          <Row className="create-account-user__rows create-account-user__col-margin-top">
            <Col className="create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.PLACEHOLDER_YEAR'
                )}
                onChange={(value) => {
                  if (typeof value === 'string') {
                    setOccupationsData({
                      ...occupationsData,
                      years: value,
                    });
                  }
                }}
                options={yearsArray.map((line) => ({
                  name: line.name,
                  value: line.value,
                }))}
              />
            </Col>

            <Col className="create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.PLACEHOLDER_MONTH'
                )}
                onChange={(name) => {
                  if (typeof name === 'string') {
                    setOccupationsData({
                      ...occupationsData,
                      months: name,
                    });
                  }
                }}
                options={monthsArray.map((line) => ({
                  name: line.name,
                  value: line.value,
                }))}
              />
            </Col>
          </Row>
        ) : null}

        <Row className="create-account-user__rows create-account-user__col-margin-top">
          <Col className="create-account-user__full">
            {onboarding.allowEducation ? (
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.EDUCATION'
                )}
                onChange={(value) => {
                  if (typeof value === 'number') {
                    setUserDataForm({
                      ...userData,
                      educationLevel: value,
                    });
                  }
                }}
                options={settings?.educationLevel?.map((level) => ({
                  value: level.id,
                  name: level.description,
                }))}
              />
            ) : null}
          </Col>

          {onboarding.allowNationality ? (
            <Col className="create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.NATIONALITY'
                )}
                onChange={(value) => {
                  if (typeof value === 'number') {
                    setUserDataForm({
                      ...userData,
                      nationality: value,
                    });
                  }
                }}
                options={(countriesList ?? []).map((type) => ({
                  name: type.nome,
                  value: type.id,
                }))}
              />
            </Col>
          ) : null}
        </Row>

        {onboarding.allowNationality ? (
          <Row className="create-account-user__rows create-account-user__col-margin-top">
            {userData.nationality === CountriesEnum.BRL && (
              <Col className="create-account-user__full">
                <AdvancedAutocomplete
                  variant="standard"
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.NATIONALITY_STATE'
                  )}
                  onChange={(value) => handleStateChange(value)}
                  options={(statesList ?? []).map((type) => ({
                    name: type.nome,
                    value: type.sigla,
                  }))}
                />
              </Col>
            )}
            {onboarding.allowNationality &&
            userData.nationality === CountriesEnum.BRL ? (
              <Col className="create-account-user__full">
                <AdvancedAutocomplete
                  variant="standard"
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.NATIONALITY_CITY'
                  )}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      setUserDataForm({
                        ...userData,
                        bornCity: value,
                      });
                    }
                  }}
                  options={(citiesList ?? []).map((type) => ({
                    value: type.nome,
                    name: type.nome,
                  }))}
                />
              </Col>
            ) : null}
          </Row>
        ) : null}

        <Row className="create-account-user__rows create-account-user__col-margin-top">
          {onboarding.allowMaritalStatus ? (
            <Col className="create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.MARITAL_STATUS'
                )}
                onChange={(value) => {
                  if (typeof value === 'number') {
                    setUserDataForm({
                      ...userData,
                      maritalStatus: value,
                    });
                  }
                }}
                options={settings?.maritalStatus?.map((status) => ({
                  value: status.id,
                  name: status.type,
                }))}
              />
            </Col>
          ) : null}

          {onboarding.allowSpouseName &&
          userData.maritalStatus === MaritalStatus.MARRIED ? (
            <Col className="create-account-user__full">
              <AdvancedInput
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.SPOUSE_NAME'
                )}
                value={userData.spouseName}
                variant="standard"
                onChange={(value) => {
                  if (typeof value === 'string') {
                    setUserDataForm({
                      ...userData,
                      spouseName: value,
                    });
                  }
                }}
              />
            </Col>
          ) : null}
        </Row>

        {onboarding.allowSpouseCPF &&
        userData.maritalStatus === MaritalStatus.MARRIED ? (
          <Row className="create-account-user__rows create-account-user__col-margin-top">
            <Col className="create-account-user__full">
              <AdvancedInput
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.SPOUSE_CPF'
                )}
                value={userData.spouseCpf ? maskCpf(userData.spouseCpf) : null}
                onChange={(value) => {
                  if (unmaskField(value).length <= 14) {
                    setUserDataForm({
                      ...userData,
                      spouseCpf: value,
                    });
                  }
                }}
              />
            </Col>
          </Row>
        ) : null}

        {onboarding.allowFATCA ? (
          <>
            <Row className="create-account-user__rows create-account-user__col-margin-top">
              <p className="create-account-user__text">
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.FATCA.TITLE'
                )}
              </p>
            </Row>
            <Row className="create-account-user__rows create-account-user__col-margin-top">
              <Col className="create-account-user__full">
                <AdvancedCheckbox
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.FATCA.TAX_DOMICILE'
                  )}
                  value={hasFiscalResidenciaOutside}
                  onChange={() =>
                    setHasFiscalResidenciaOutside(!hasFiscalResidenciaOutside)
                  }
                />
              </Col>

              <Col className="create-account-user__full">
                <AdvancedCheckbox
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.FATCA.US_PERSON'
                  )}
                  value={isUsPerson}
                  onChange={() => setIsUsPerson(!isUsPerson)}
                />

                <div>
                  <Popover content={PopOverContent}>
                    <QuestionCircleOutlined />
                  </Popover>
                </div>
              </Col>
            </Row>
          </>
        ) : null}
      </AdvancedForm>
    </div>
  );
};

export default OnboardingCbo;
